import { CBSocketClient, IS_SOCKET_ERROR, SocketClient } from "cbcore-ts"
import { IF, nil, NO, UIButton, UIColor, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { RHUserPasswordPolicy } from "../Core/RHUserPasswordPolicy"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHForgotPasswordViewTokens, RHLoginViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHTextField } from "../Custom components/RH/RHTextField"
import { LoginViewController } from "./LoginViewController"
import { messageOfError } from "../Core/RHErrorMessage"
import { RHUserName } from "../Core/RHUserName"
import { RHUserNameInputView } from "../Custom components/RH/RHUserNameInputView"

const technicalRegistrationErrorMessage = "Tehniline tõrge. Kui viga püsib, võta ühendust aadressil info@rhr.ee."

function registrationErrorMessageFromResponse(response: { errorResult?: unknown } | undefined, email: string): string {

    const errorResult = response?.errorResult
    const messageData = IS_SOCKET_ERROR(errorResult) ? errorResult.messageData : errorResult
    const message = typeof messageData === "string" ? messageData : messageOfError(messageData)

    if (typeof message !== "string") {
        return technicalRegistrationErrorMessage
    }

    if (message === "Email in use." || message === "E-posti aadress on kasutuses") {
        return `E-posti aadress „${email}“ on kasutuses.`
    }

    if (message.startsWith("Invalid email: ")) {
        return `E-posti aadress „${message.slice("Invalid email: ".length)}“ on vigane.`
    }

    if (message.startsWith("e-posti aadress ei ole korrektne ")) {
        return `E-posti aadress „${message.slice("e-posti aadress ei ole korrektne ".length)}“ on vigane.`
    }

    if (message === "Kutselink on aegunud või vigane." || message === "Kutselink on aegunud või vigane") {
        return "Kutse on aegunud või link on vigane"
    }

    if (message.startsWith("An unexpected server error occurred.")) {
        return technicalRegistrationErrorMessage
    }

    return message.endsWith(".") ? message.slice(0, -1) : message
}

export class RegisterUserViewController extends UIViewController {
    
    // Stored when the user arrives via a procurement invitation link.
    // Passed to RegisterNewUser so the server can pre-verify the email.
    _invitationToken?: string
    
    readonly invitationBannerView = new RHAlertBannerView().configuredWithObject({
        alertType: RHAlertBannerView.type.Info
    })
    
    readonly titleLabel: UITextView = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Loo kasutaja",
        hoverText: "",
        backgroundColor: UIColor.transparentColor,
        textColor: RHColors.textPrimary,
        textAlignment: UITextView.textAlignment.center,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeXXLarge,
            fontWeight: RHForgotPasswordViewTokens.titleFontWeight,
            lineHeight: RHFont.lineHeightHeading
        }
    }).addedAsSubviewToView(this.view)
    
    usernameTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "E-posti aadress",
        autocompleteEnabled: NO,
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                this.nameInputView.firstNameTextField.focus()
            }
        }
    })

    readonly nameInputView = new RHUserNameInputView().configuredWithObject({
        lastNameTextField: {
            controlEventTargetAccumulator: {
                EnterDown: () => {
                    this.passwordTextField.focus()
                }
            }
        }
    }).addedAsSubviewToView(this.view)
    
    passwordTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Salasõna",
        type: "password",
        autocompleteEnabled: NO,
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                this.passwordTextFieldAgain.focus()
            }
        }
    })
    
    passwordTextFieldAgain = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Korda salasõna",
        type: "password",
        autocompleteEnabled: NO,
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                this.registerButton.sendControlEventForKey(UIButton.controlEvent.EnterDown, nil)
            }
        }
    })
    
    registerButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Registreeri"
        },
        variant: "success",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async (sender, event) => {
                const nameComponents = this.nameInputView.nameComponents
                const nameError = RHUserName.failedRequirementMessageForComponents(nameComponents)
                if (nameError) {
                    CBDialogViewShower.alert(nameError)
                    return
                }
                if (this.passwordTextField.text != this.passwordTextFieldAgain.text) {
                    CBDialogViewShower.alert("Salasõnad ei kattu.")
                    return
                }

                if (!RHUserPasswordPolicy.passwordMeetsMinimumLength(this.passwordTextField.text)) {
                    CBDialogViewShower.alert(RHUserPasswordPolicy.minimumLengthRequirementMessage())
                    return
                }

                if (!RHUserPasswordPolicy.passwordMeetsMaximumLength(this.passwordTextField.text)) {
                    CBDialogViewShower.alert(RHUserPasswordPolicy.maximumLengthRequirementMessage())
                    return
                }

                if (!RHUserPasswordPolicy.passwordAvoidsCommonPatterns(this.passwordTextField.text)) {
                    CBDialogViewShower.alert(RHUserPasswordPolicy.commonPatternRequirementMessage())
                    return
                }

                const response = (await SocketClient.RegisterNewUser(
                    {
                        email: this.usernameTextField.text,
                        password: this.passwordTextField.text,
                        ...nameComponents,
                        invitationToken: this._invitationToken
                    },
                    CBSocketClient.completionPolicy.firstOnly
                ))
                
                if (response.result?.accepted) {
                    
                    if (response.result.requiresEmailConfirmation) {
                        CBDialogViewShower.alert(
                            "Registreerimine õnnestus. " +
                            "Palun kontrolli oma postkasti ja kinnita konto enne sisselogimist."
                        )
                        UIRoute.currentRoute
                            .routeByRemovingComponentNamed(RegisterUserViewController.routeComponentName)
                            .routeWithViewControllerComponent(LoginViewController, {})
                            .apply()
                    }
                    else {
                        CBDialogViewShower.alert(
                            "Konto on loodud. Saad kohe sisse logida."
                        )
                        UIRoute.currentRoute
                            .routeByRemovingComponentNamed(RegisterUserViewController.routeComponentName)
                            .routeWithViewControllerComponent(
                                LoginViewController,
                                {
                                    email: this.usernameTextField.text,
                                    emailConfirmed: "YES"
                                }
                            ).apply()
                    }
                    
                    
                    
                }
                else {
                    
                    console.log("Registreerimine ebaõnnestus. ", response)
                    CBDialogViewShower.alert(registrationErrorMessageFromResponse(response, this.usernameTextField.text))
                    
                }
                
            }
        }
    })
    
    backToHomeButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Tagasi sisselogimise lehele" },
        variant: "link",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(RegisterUserViewController.routeComponentName)
                    .routeWithViewControllerComponent(LoginViewController, nil)
                    .apply()
            }
        }
    })
    
    bottomView = new UIView().addedAsSubviewToView(this.view)
    
    override _focusManager = new RHFocusManager(() => [
        this.usernameTextField,
        this.nameInputView.firstNameTextField,
        this.nameInputView.lastNameTextField,
        this.passwordTextField,
        this.passwordTextFieldAgain,
        this.registerButton,
        this.backToHomeButton
    ])
    
    constructor(view: UIView) {
        
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor,
            hoverText: ""
        })
        
    }
    
    
    static override readonly routeComponentName = "register"
    
    static override readonly ParameterIdentifierName = {
        invitationToken: "invitationToken"
    }
    
    override async viewDidAppear() {
        
        await super.viewDidAppear()
        
        if (!this._invitationToken) {
            this.usernameTextField.focus()
        }
        
    }
    
    
    override async viewWillAppear() {
        
        await super.viewWillAppear()
        
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
        
    }
    
    
    override async viewWillDisappear() {
        
        await super.viewWillDisappear()
        
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
        
    }
    
    
    override async handleRoute(route: UIRoute) {
        
        super.handleRoute(route)
        
        const component = route.componentWithName(RegisterUserViewController.routeComponentName)
        const token = component?.parameters[RegisterUserViewController.ParameterIdentifierName.invitationToken]

        this.registerButton.enabled = YES
        this.passwordTextField.addedAsSubviewToView(this.view)
        this.passwordTextFieldAgain.addedAsSubviewToView(this.view)
        
        if (!token) {
            this._invitationToken = undefined
            this.invitationBannerView.removeFromSuperview()
            this.view.setNeedsLayoutUpToRootView()
            return
        }
        
        // If a user is currently logged in, install the invitation route before
        // the logout broadcast so no stale authenticated route can win the race.
        if (RHCore.sharedInstance.isUserLoggedIn) {
            const registrationRoute = route
                .routeByRemovingParameterInComponent("settings", "mode", YES)
                .routeWithViewControllerComponent(
                    RegisterUserViewController,
                    { invitationToken: token }
                )
            await RHCore.sharedInstance.logOutAndApplyRoute(registrationRoute)
            return
        }
        
        this._invitationToken = token
        
        // Redeem the token to learn the email and procurement context
        const response = await SocketClient.RedeemProcurementInvitationToken(
            { token },
            CBSocketClient.completionPolicy.firstOnly
        )
        
        if (response.result) {
            this.usernameTextField.text = response.result.email
            this.invitationBannerView.configureWithObject({
                text: `✉️ Sind on kutsutud osalema hankes „${
                    RHTextSanitizer.escapedHTML(response.result.procurementTitle)
                }“. ` +
                    `E-posti aadress on eeltäidetud. Registreeru, et esitada pakkumus.`
            })
            if (this.invitationBannerView.superview != this.view) {
                this.invitationBannerView.addedAsSubviewToView(this.view)
            }
            this.nameInputView.firstNameTextField.focus()
        }
        else {
            // Token invalid or expired — fall back to plain registration
            this._invitationToken = undefined
            this.invitationBannerView.removeFromSuperview()
            CBDialogViewShower.alert("Kutse on aegunud või link on vigane. Saad siiski registreeruda.")
        }
        
        this.view.setNeedsLayoutUpToRootView()
        
    }


    override updateViewConstraints() {
        
        super.updateViewConstraints()
        
    }
    
    
    override updateViewStyles() {
        
        super.updateViewStyles()
        
    }
    
    
    override viewDidLayoutSubviews() {
        
        super.viewDidLayoutSubviews()
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        const contentBounds = bounds.rectangleWithMaxWidth(RHLoginViewTokens.authContentMaxWidth, 0.5)
        
        const registerButtonWidth = [
            [
                this.registerButton.titleLabel.intrinsicContentWidth(RHForgotPasswordViewTokens.submitButtonHeight) +
                RHButtons.contentPadding * 2 +
                RHSpacing.xLarge,
                RHForgotPasswordViewTokens.submitButtonMinWidth
            ].max(),
            RHForgotPasswordViewTokens.submitButtonMaxWidth,
            contentBounds.width
        ].min()
        
        contentBounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .IF(this.view.hasSubview(this.invitationBannerView))
            .rectangleForNextRow(padding, this.invitationBannerView)
            .assignedAsFrameOfView(this.invitationBannerView)
            .ENDIF()
            .rectangleForNextRow(padding * 2, [
                this.usernameTextField.intrinsicContentHeight(contentBounds.width),
                RHForgotPasswordViewTokens.fieldMinHeight
            ].max())
            .assignedAsFrameOfView(this.usernameTextField)
            .rectangleForNextRow(padding, this.nameInputView)
            .assignedAsFrameOfView(this.nameInputView)
            .rectangleForNextRow(padding, [
                this.passwordTextField.intrinsicContentHeight(contentBounds.width),
                RHForgotPasswordViewTokens.fieldMinHeight
            ].max())
            .assignedAsFrameOfView(this.passwordTextField)
            .rectangleForNextRow(padding, [
                this.passwordTextFieldAgain.intrinsicContentHeight(contentBounds.width),
                RHForgotPasswordViewTokens.fieldMinHeight
            ].max())
            .assignedAsFrameOfView(this.passwordTextFieldAgain)
            .rectangleForNextRow(padding, RHForgotPasswordViewTokens.submitButtonHeight)
            .rectangleWithWidth(registerButtonWidth, 0.5)
            .assignedAsFrameOfView(this.registerButton)
            .rectangleWithWidth(contentBounds.width, 0.5)
            .rectangleForNextRow(padding, RHForgotPasswordViewTokens.submitButtonHeight)
            .assignedAsFrameOfView(this.backToHomeButton)
            .rectangleForNextRow(padding, 1)
            .assignedAsFrameOfView(this.bottomView)
        
    }
    
}
